import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ChecklistTemplates.css';
import api from '../services/api';

const generateId = () => 'temp_' + Math.random().toString(36).substr(2, 9);

const ITEM_TYPES = [
  { value: 'koptekst', label: 'Koptekst / Sectie', icon: '🔷', color: '#1e40af', desc: 'Sectie header' },
  { value: 'goed_fout_nvt', label: 'Goed / Fout / NVT', icon: '✓', color: '#16a34a', desc: '3 keuzes' },
  { value: 'goed_fout', label: 'Goed / Fout', icon: '✓', color: '#2563eb', desc: '2 keuzes' },
  { value: 'tekst', label: 'Tekstveld', icon: '📝', color: '#8b5cf6', desc: 'Vrije tekst invoer' },
  { value: 'nummer', label: 'Nummer', icon: '🔢', color: '#f59e0b', desc: 'Alleen cijfers' },
  { value: 'nummer_eenheid', label: 'Nummer + Eenheid', icon: '📏', color: '#ea580c', desc: 'Getal met eenheid' },
  { value: 'foto', label: 'Foto', icon: '📷', color: '#ec4899', desc: 'Camera / upload' },
  { value: 'handtekening', label: 'Handtekening', icon: '✍️', color: '#6366f1', desc: 'Digitale handtekening' },
  { value: 'dropdown', label: 'Dropdown keuze', icon: '▼', color: '#0891b2', desc: 'Kies uit opties' },
  { value: 'datum', label: 'Datum', icon: '📅', color: '#059669', desc: 'Datum kiezen' },
  { value: 'tijd', label: 'Tijd', icon: '🕐', color: '#0d9488', desc: 'Tijd invoeren' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑', color: '#65a30d', desc: 'Aanvinken' },
  { value: 'email', label: 'E-mail', icon: '✉', color: '#7c3aed', desc: 'E-mailadres' },
  { value: 'ip_adres', label: 'IP Adres', icon: '🌐', color: '#0284c7', desc: 'IP adres invoer' },
];

function ChecklistTemplates() {
  const navigate = useNavigate();
  const { gebruiker } = useAuth();

  // List state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [catFilter, setCatFilter] = useState('__alle');
  const [catEdit, setCatEdit] = useState(null);
  const slaCategorieOp = async (tpl, waarde) => {
    setCatEdit(null);
    const cat = (waarde || '').trim() || null;
    if ((tpl.categorie || null) === cat) return;
    try {
      await api.put('/checklists/templates/' + tpl.id + '/categorie', { categorie: cat });
      setTemplates(ts => ts.map(t => t.id === tpl.id ? { ...t, categorie: cat } : t));
    } catch (e) { alert(e.response?.data?.message || 'Opslaan categorie mislukt'); }
  };

  // Editor state
  const [activeTemplate, setActiveTemplate] = useState(null); // null = list view, object = editor
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddItem, setShowAddItem] = useState(null);
  const [optionInput, setOptionInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [blokKiezer, setBlokKiezer] = useState(false);
  const [beschikbareBlokken, setBeschikbareBlokken] = useState([]);
  const [gekozenBlokken, setGekozenBlokken] = useState([]);
  const openBlokKiezer = async () => {
    setGekozenBlokken([]);
    try { const r = await api.get('/checklists/blokken'); setBeschikbareBlokken(r.data.blokken || []); }
    catch (e) { setBeschikbareBlokken([]); }
    setBlokKiezer(true);
  };
  const voegBlokToe = (b) => {
    setGekozenBlokken(prev => [...prev, { key: generateId(), id: b.id, naam: b.naam }]);
  };
  const verwijderGekozen = (key) => {
    setGekozenBlokken(prev => prev.filter(x => x.key !== key));
  };
  const verplaatsBlok = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= gekozenBlokken.length) return;
    const arr = [...gekozenBlokken];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setGekozenBlokken(arr);
  };
  const voegBlokkenIn = async () => {
    try {
      // haal elk uniek blok maar 1x op (cache), ook als het meerdere keren gekozen is
      const cache = {};
      for (const gb of gekozenBlokken) {
        if (!cache[gb.id]) {
          const r = await api.get('/checklists/blokken/' + gb.id);
          cache[gb.id] = (r.data.items || []);
        }
      }
      // nummering per bloknaam: 1e = "FHK", 2e = "FHK 2", 3e = "FHK 3"
      const telPerNaam = {};
      const nieuweSecties = gekozenBlokken.map(gb => {
        telPerNaam[gb.naam] = (telPerNaam[gb.naam] || 0) + 1;
        const naam = telPerNaam[gb.naam] === 1 ? gb.naam : (gb.naam + ' ' + telPerNaam[gb.naam]);
        const its = (cache[gb.id] || []).map(it => ({
          id: generateId(), beschrijving: it.label, type: it.type,
          verplicht: it.verplicht !== false, eenheid: it.eenheid || '',
          placeholder: it.placeholder || '',
          opties: it.dropdown_opties ? it.dropdown_opties.split('|') : [],
          kolom: it.kolom || 'links',
        }));
        return { id: generateId(), naam, collapsed: false, items: its };
      });
      const bestaand = (activeTemplate.secties || []).filter(
        s => !(s.items.length === 0 && (s.naam === 'Algemeen' || s.naam === 'Nieuwe sectie'))
      );
      updateActive({ secties: [...bestaand, ...nieuweSecties] });
      setBlokKiezer(false);
    } catch (e) { alert('Invoegen mislukt: ' + (e.response?.data?.message || e.message)); }
  };

  const [uploadForm, setUploadForm] = useState({ naam: '', beschrijving: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // New template modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // Drag state
  const dragRef = useRef({ type: null, sectionId: null, itemId: null });
  const [dragIndicator, setDragIndicator] = useState(null);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragSectionIndicator, setDragSectionIndicator] = useState(null);
  const [draggingSectionId, setDraggingSectionId] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  // ══════════════════════════════════════
  // ══  API CALLS
  // ══════════════════════════════════════

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/checklists/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Fout bij laden templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetail = async (templateId) => {
    try {
      const response = await api.get('/checklists/templates/' + templateId);
      const tpl = response.data.template;
      const secties = response.data.secties || [];
      const items = response.data.items || [];

      // Converteer naar editor formaat
      const editorSecties = secties.map(sec => ({
        id: sec.id.toString(),
        naam: sec.naam,
        aanpasbaar: !!sec.aanpasbaar, min_aantal: sec.min_aantal ?? 1, standaard_aantal: sec.standaard_aantal ?? 1,
        max_aantal: sec.max_aantal ?? 1, naam_invulbaar: !!sec.naam_invulbaar,
        collapsed: false,
        items: items
          .filter(i => i.sectie_id === sec.id && i.type !== 'koptekst')
          .sort((a, b) => a.volgorde - b.volgorde)
          .map(i => ({
            id: i.id.toString(),
            beschrijving: i.label,
            type: i.type,
            verplicht: i.verplicht || false,
            eenheid: i.eenheid || '',
            placeholder: i.placeholder || '',
            opties: i.dropdown_opties ? i.dropdown_opties.split('|') : [],
            kolom: i.kolom || 'links',
          }))
      }));

      // Items zonder sectie → aparte sectie
      const losseItems = items.filter(i => !i.sectie_id && i.type !== 'koptekst');
      if (losseItems.length > 0) {
        editorSecties.unshift({
          id: 'unsorted',
          naam: 'Algemeen',
          collapsed: false,
          items: losseItems.map(i => ({
            id: i.id.toString(),
            beschrijving: i.label,
            type: i.type,
            verplicht: i.verplicht || false,
            eenheid: i.eenheid || '',
            placeholder: i.placeholder || '',
            opties: i.dropdown_opties ? i.dropdown_opties.split('|') : [],
            kolom: i.kolom || 'links',
          }))
        });
      }

      // Als geen secties, maak er één
      if (editorSecties.length === 0) {
        editorSecties.push({ id: generateId(), naam: 'Algemeen', collapsed: false, items: [] });
      }

      setActiveTemplate({
        id: tpl.id,
        naam: tpl.naam,
        beschrijving: tpl.beschrijving || '',
        kleur: tpl.kleur || '#1e40af',
        twoColumn: tpl.two_column || false,
        soort: tpl.soort || 'checklist',
        handtekening: tpl.handtekening || 'geen',
        secties: editorSecties,
        isNew: false,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Fout bij laden template:', error);
      alert('Fout bij laden template details');
    }
  };

  const saveTemplate = async () => {
    if (!activeTemplate) return;
    setSaving(true);
    try {
      const payload = {
        naam: activeTemplate.naam,
        beschrijving: activeTemplate.beschrijving,
        kleur: activeTemplate.kleur,
        twoColumn: activeTemplate.twoColumn,
        soort: activeTemplate.soort || 'checklist',
        handtekening: activeTemplate.handtekening || 'geen',
        secties: activeTemplate.secties.map(sec => ({
          naam: sec.naam,
          aanpasbaar: !!sec.aanpasbaar, min_aantal: sec.min_aantal, standaard_aantal: sec.standaard_aantal,
          max_aantal: sec.max_aantal, naam_invulbaar: !!sec.naam_invulbaar,
          items: sec.items.map(item => ({
            beschrijving: item.beschrijving,
            type: item.type,
            verplicht: item.verplicht || false,
            eenheid: item.eenheid || null,
            placeholder: item.placeholder || null,
            opties: item.opties || [],
            kolom: item.kolom || 'links',
          }))
        }))
      };

      if (activeTemplate.isNew) {
        const response = await api.post('/checklists/templates/create', payload);
        setActiveTemplate(prev => ({ ...prev, id: response.data.template.id, isNew: false }));
      } else {
        await api.put('/checklists/templates/' + activeTemplate.id + '/full', payload);
      }

      setHasChanges(false);
      loadTemplates();
    } catch (error) {
      console.error('Fout bij opslaan:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const duplicateTemplate = async (templateId) => {
    try {
      await api.post('/checklists/templates/' + templateId + '/duplicate');
      loadTemplates();
    } catch (error) {
      alert('Fout bij dupliceren');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Template verwijderen? Dit kan niet ongedaan worden.')) return;
    try {
      await api.delete('/checklists/templates/' + templateId);
      loadTemplates();
    } catch (error) {
      alert('Fout bij verwijderen');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.naam) { alert('Selecteer een bestand en vul een naam in'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('naam', uploadForm.naam);
      formData.append('beschrijving', uploadForm.beschrijving);
      const response = await api.post('/checklists/templates/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Template aangemaakt met ' + response.data.items_count + ' items!');
      setShowUploadModal(false);
      setUploadForm({ naam: '', beschrijving: '' });
      setSelectedFile(null);
      loadTemplates();
    } catch (error) {
      alert('Fout bij uploaden: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // ══════════════════════════════════════
  // ══  EDITOR FUNCTIONS
  // ══════════════════════════════════════

  const updateActive = (changes) => {
    setActiveTemplate(prev => ({ ...prev, ...changes }));
    setHasChanges(true);
  };

  // Sections
  const addSection = () => {
    updateActive({ secties: [...activeTemplate.secties, { id: generateId(), naam: 'Nieuwe sectie', collapsed: false, items: [] }] });
  };

  const updateSection = (sId, changes) => {
    updateActive({ secties: activeTemplate.secties.map(s => s.id === sId ? { ...s, ...changes } : s) });
  };

  const deleteSection = (sId) => {
    if (activeTemplate.secties.length <= 1) return;
    updateActive({ secties: activeTemplate.secties.filter(s => s.id !== sId) });
  };

  const duplicateSection = (sId) => {
    const idx = activeTemplate.secties.findIndex(s => s.id === sId);
    if (idx === -1) return;
    const orig = activeTemplate.secties[idx];
    const kopie = {
      ...orig,
      id: generateId(),
      naam: orig.naam + ' (kopie)',
      collapsed: false,
      items: (orig.items || []).map(it => ({ ...it, id: generateId() }))
    };
    const arr = [...activeTemplate.secties];
    arr.splice(idx + 1, 0, kopie);
    updateActive({ secties: arr });
  };
  const moveSection = (sId, dir) => {
    const idx = activeTemplate.secties.findIndex(s => s.id === sId);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === activeTemplate.secties.length - 1)) return;
    const arr = [...activeTemplate.secties];
    [arr[idx], arr[idx + dir]] = [arr[idx + dir], arr[idx]];
    updateActive({ secties: arr });
  };

  // Items
  const addItem = (sId, type) => {
    const newItem = {
      id: generateId(), beschrijving: 'Nieuw item', type, verplicht: true,
      eenheid: '', placeholder: '', opties: type === 'dropdown' ? ['Optie 1', 'Optie 2'] : [], kolom: 'links',
    };
    updateActive({
      secties: activeTemplate.secties.map(s => s.id === sId ? { ...s, items: [...s.items, newItem] } : s)
    });
    setShowAddItem(null);
    setEditingItem(newItem.id);
  };

  const updateItem = (sId, iId, changes) => {
    updateActive({
      secties: activeTemplate.secties.map(s => s.id === sId ? { ...s, items: s.items.map(i => i.id === iId ? { ...i, ...changes } : i) } : s)
    });
  };

  const deleteItem = (sId, iId) => {
    updateActive({
      secties: activeTemplate.secties.map(s => s.id === sId ? { ...s, items: s.items.filter(i => i.id !== iId) } : s)
    });
    if (editingItem === iId) setEditingItem(null);
  };

  const moveItem = (sId, iId, dir) => {
    const sec = activeTemplate.secties.find(s => s.id === sId);
    const idx = sec.items.findIndex(i => i.id === iId);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === sec.items.length - 1)) return;
    const arr = [...sec.items];
    [arr[idx], arr[idx + dir]] = [arr[idx + dir], arr[idx]];
    updateActive({ secties: activeTemplate.secties.map(s => s.id === sId ? { ...s, items: arr } : s) });
  };

  // ══════════════════════════════════════
  // ══  DRAG & DROP
  // ══════════════════════════════════════

  const onItemDragStart = (e, sId, iId) => {
    dragRef.current = { type: 'item', sectionId: sId, itemId: iId };
    setDraggingItemId(iId);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.opacity = '0';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const onItemDragEnd = () => {
    dragRef.current = { type: null, sectionId: null, itemId: null };
    setDraggingItemId(null);
    setDragIndicator(null);
  };

  const onDropZoneDragOver = (e, sectionId, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragRef.current.type !== 'item') return;
    e.dataTransfer.dropEffect = 'move';
    const srcSec = dragRef.current.sectionId;
    const srcItem = dragRef.current.itemId;
    if (srcSec === sectionId) {
      const sec = activeTemplate.secties.find(s => s.id === sectionId);
      const srcIdx = sec.items.findIndex(i => i.id === srcItem);
      if (index === srcIdx || index === srcIdx + 1) { setDragIndicator(null); return; }
    }
    setDragIndicator({ sectionId, index });
  };

  const onDropZoneDrop = (e, sectionId, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragRef.current.type !== 'item') return;
    const srcSecId = dragRef.current.sectionId;
    const srcItemId = dragRef.current.itemId;
    const data = JSON.parse(JSON.stringify(activeTemplate));
    let moved = null;
    let srcIdx = -1;
    data.secties = data.secties.map(s => {
      if (s.id === srcSecId) {
        srcIdx = s.items.findIndex(i => i.id === srcItemId);
        if (srcIdx > -1) { moved = s.items[srcIdx]; s.items.splice(srcIdx, 1); }
      }
      return s;
    });
    if (!moved) return;
    let targetIdx = index;
    if (srcSecId === sectionId && srcIdx < index) targetIdx = index - 1;
    data.secties = data.secties.map(s => {
      if (s.id === sectionId) s.items.splice(targetIdx, 0, moved);
      return s;
    });
    setActiveTemplate(data);
    setHasChanges(true);
    setDragIndicator(null);
    setDraggingItemId(null);
    dragRef.current = { type: null, sectionId: null, itemId: null };
  };

  const onSectionBodyDragOver = (e, sectionId) => {
    e.preventDefault();
    if (dragRef.current.type !== 'item') return;
    const sec = activeTemplate.secties.find(s => s.id === sectionId);
    if (sec.items.length === 0) setDragIndicator({ sectionId, index: 0 });
  };

  const onEmptySectionDrop = (e, sId) => {
    e.preventDefault();
    if (dragRef.current.type !== 'item' || !dragRef.current.itemId) return;
    const data = JSON.parse(JSON.stringify(activeTemplate));
    let moved = null;
    data.secties = data.secties.map(s => {
      if (s.id === dragRef.current.sectionId) {
        const idx = s.items.findIndex(i => i.id === dragRef.current.itemId);
        if (idx > -1) { moved = s.items[idx]; s.items.splice(idx, 1); }
      }
      return s;
    });
    if (moved) {
      data.secties = data.secties.map(s => { if (s.id === sId) s.items.push(moved); return s; });
      setActiveTemplate(data);
      setHasChanges(true);
    }
    setDragIndicator(null);
    setDraggingItemId(null);
  };

  // Section drag
  const onSecDragStart = (e, sId) => {
    dragRef.current = { type: 'section', sectionId: sId, itemId: null };
    setDraggingSectionId(sId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onSecDragEnd = () => {
    dragRef.current = { type: null, sectionId: null, itemId: null };
    setDraggingSectionId(null);
    setDragSectionIndicator(null);
  };
  const onSecDropZoneDragOver = (e, index) => {
    e.preventDefault();
    if (dragRef.current.type !== 'section') return;
    const srcIdx = activeTemplate.secties.findIndex(s => s.id === dragRef.current.sectionId);
    if (index === srcIdx || index === srcIdx + 1) { setDragSectionIndicator(null); return; }
    setDragSectionIndicator(index);
  };
  const onSecDropZoneDrop = (e, index) => {
    e.preventDefault();
    if (dragRef.current.type !== 'section') return;
    const data = JSON.parse(JSON.stringify(activeTemplate));
    const srcIdx = data.secties.findIndex(s => s.id === dragRef.current.sectionId);
    if (srcIdx === -1) return;
    let targetIdx = index;
    if (srcIdx < index) targetIdx = index - 1;
    const [moved] = data.secties.splice(srcIdx, 1);
    data.secties.splice(targetIdx, 0, moved);
    setActiveTemplate(data);
    setHasChanges(true);
    setDragSectionIndicator(null);
    setDraggingSectionId(null);
  };

  // ══════════════════════════════════════
  // ══  HELPERS
  // ══════════════════════════════════════

  const totalItems = activeTemplate ? activeTemplate.secties.reduce((a, s) => a + s.items.length, 0) : 0;
  const requiredItems = activeTemplate ? activeTemplate.secties.reduce((a, s) => a + s.items.filter(i => i.verplicht).length, 0) : 0;

  const typeTag = (type) => {
    const t = ITEM_TYPES.find(x => x.value === type);
    if (!t) return null;
    return (
      <span style={{ padding: '3px 8px', borderRadius: 5, background: t.color + '18', color: t.color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {t.icon} {t.label}
      </span>
    );
  };

  const renderItemPreview = (item) => {
    const s = { fontSize: 12, color: '#9ca3af' };
    switch (item.type) {
      case 'goed_fout_nvt': return <div style={{ display: 'flex', gap: 5 }}>{['Goed','Fout','NVT'].map((l,i) => <div key={i} style={{ flex: 1, padding: '7px 4px', border: '2px solid #e2e8f0', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, color: i===0?'#16a34a':i===1?'#dc2626':'#6b7280' }}>{l}</div>)}</div>;
      case 'goed_fout': return <div style={{ display: 'flex', gap: 5 }}>{['Goed','Fout'].map((l,i) => <div key={i} style={{ flex: 1, padding: '7px 4px', border: '2px solid #e2e8f0', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, color: i===0?'#16a34a':'#dc2626' }}>{l}</div>)}</div>;
case 'goed_fout_ip': return <div><div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>{['Goed','Fout'].map((l,i) => <div key={i} style={{ flex: 1, padding: '7px 4px', border: '2px solid #e2e8f0', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, color: i===0?'#16a34a':'#dc2626' }}>{l}</div>)}</div><div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, color: '#9ca3af' }}>192.168.1.1</div></div>;
      case 'aanwezig_afwezig': return <div style={{ display: 'flex', gap: 5 }}>{['Aanwezig','Niet aanwezig'].map((l,i) => <div key={i} style={{ flex: 1, padding: '7px 4px', border: '2px solid #e2e8f0', borderRadius: 6, textAlign: 'center', fontSize: 11, fontWeight: 600, color: i===0?'#16a34a':'#dc2626' }}>{l}</div>)}</div>;
      case 'tekst': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>{item.placeholder || 'Vul in...'}</div>;
      case 'nummer': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>0</div>;
      case 'nummer_eenheid': return <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>0</div>{item.eenheid && <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 12 }}>{item.eenheid}</span>}</div>;
      case 'foto': return <div style={{ padding: 14, border: '2px dashed #d1d5db', borderRadius: 8, textAlign: 'center', ...s }}>📷 Tik om foto te maken</div>;
      case 'handtekening': return <div style={{ padding: 14, border: '2px dashed #d1d5db', borderRadius: 8, textAlign: 'center', ...s, background: '#fafafa' }}>✍️ Tik om te tekenen</div>;
      case 'dropdown': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, display: 'flex', justifyContent: 'space-between', ...s }}><span>{item.opties?.[0] || 'Kies...'}</span><span>▼</span></div>;
      case 'datum': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>📅 dd-mm-jjjj</div>;
      case 'tijd': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>🕐 00:00</div>;
      case 'checkbox': return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 22, height: 22, borderRadius: 4, border: '2px solid #d1d5db' }} /><span style={s}>Aanvinken</span></div>;
      case 'email': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>naam@voorbeeld.nl</div>;
      case 'ip_adres': return <div style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, ...s }}>192.168.1.1</div>;
      default: return null;
    }
  };

  const goBack = () => {
    if (hasChanges && !window.confirm('Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je terug wilt?')) return;
    setActiveTemplate(null);
    setShowPreview(false);
    setShowSettings(false);
    setEditingItem(null);
    setHasChanges(false);
  };

  const startNewTemplate = () => {
    if (!newName.trim()) return;
    setActiveTemplate({
      id: null,
      naam: newName.trim(),
      beschrijving: newDesc.trim(),
      kleur: '#1e40af',
      twoColumn: false,
      secties: [{ id: generateId(), naam: 'Algemeen', collapsed: false, items: [] }],
      isNew: true,
    });
    setShowNewModal(false);
    setNewName('');
    setNewDesc('');
    setHasChanges(true);
  };

  // ══════════════════════════════════════
  // ══  RENDER: TEMPLATE LIST
  // ══════════════════════════════════════

  if (loading) {
    return <div className="checklist-templates"><div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Templates laden...</div></div>;
  }

  if (!activeTemplate) {
    return (
      <div className="checklist-templates">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, padding: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28 }}>📋 Checklist Templates</h1>
            <button onClick={() => navigate('/checklist-blokken')} style={{ marginLeft: 12, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>🧱 Blokken beheren</button>
            <p style={{ margin: '4px 0 0', color: '#6b7280' }}>Bouw, bewerk en beheer checklist templates</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowUploadModal(true)} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
              📤 Excel Upload
            </button>
            <button onClick={() => setShowNewModal(true)} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              ＋ Nieuwe Template
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '0 20px 16px' }}>
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="🔍 Zoek template..."
            style={{ width: '100%', maxWidth: 400, padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
        </div>

        {/* Grid */}
        {templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: 16, margin: '0 20px' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📋</div>
            <h2 style={{ margin: '0 0 8px', color: '#374151' }}>Nog geen templates</h2>
            <p style={{ color: '#6b7280', marginBottom: 20 }}>Maak je eerste checklist template aan</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16, padding: '0 20px' }}>
            {(() => {
              const cats = [...new Set(templates.map(t => t.categorie).filter(c => c && c !== 'Overig'))].sort();
              const heeftOverig = templates.some(t => !t.categorie || t.categorie === 'Overig');
              const lijst = ['__alle', ...cats, ...(heeftOverig ? ['Overig'] : [])];
              return (
                <div style={{ gridColumn: '1 / -1', flexBasis: '100%', width: '100%', display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {lijst.map(c => {
                    const n = c === '__alle' ? templates.length : templates.filter(t => (t.categorie || 'Overig') === c).length;
                    const aan = catFilter === c;
                    return <button key={c} onClick={() => setCatFilter(c)} style={{ padding: '5px 12px', borderRadius: 999, border: '1px solid ' + (aan ? '#2563eb' : '#cbd5e1'), background: aan ? '#2563eb' : 'white', color: aan ? 'white' : '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{c === '__alle' ? 'Alle' : c} <span style={{ opacity: 0.7 }}>{n}</span></button>;
                  })}
                  <datalist id="tpl-cats">{cats.map(c => <option key={c} value={c} />)}</datalist>
                </div>
              );
            })()}
            {templates.filter(t => (!searchQ || t.naam.toLowerCase().includes(searchQ.toLowerCase())) && (catFilter === '__alle' || (t.categorie || 'Overig') === catFilter)).sort((x, y) => (x.categorie || '~').localeCompare(y.categorie || '~') || x.naam.localeCompare(y.naam)).map(tpl => (
              <div key={tpl.id} onClick={() => loadTemplateDetail(tpl.id)}
                style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20, border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 17, paddingRight: 60 }}>{tpl.naam}</h3>
                </div>
                {tpl.beschrijving && <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 14px', lineHeight: 1.5 }}>{tpl.beschrijving}</p>}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12, display: 'flex', gap: 12, fontSize: 13, color: '#6b7280' }}>
                  <span>{tpl.aantal_items || 0} items</span>
                  {catEdit && catEdit.id === tpl.id ? (
                    <input autoFocus list="tpl-cats" value={catEdit.waarde} placeholder="Categorie…"
                      onClick={e => e.stopPropagation()}
                      onChange={e => setCatEdit({ id: tpl.id, waarde: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') slaCategorieOp(tpl, catEdit.waarde); if (e.key === 'Escape') setCatEdit(null); }}
                      onBlur={() => catEdit && slaCategorieOp(tpl, catEdit.waarde)}
                      style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '1px solid #2563eb', width: 130, color: '#0f172a', background: 'white' }} />
                  ) : (
                    <span title="Klik om categorie te wijzigen" onClick={e => { e.stopPropagation(); setCatEdit({ id: tpl.id, waarde: tpl.categorie || '' }); }}
                      style={{ cursor: 'pointer', background: tpl.categorie ? '#f0fdf4' : '#f8fafc', color: tpl.categorie ? '#166534' : '#94a3b8', border: '1px dashed ' + (tpl.categorie ? '#86efac' : '#cbd5e1'), borderRadius: 6, padding: '1px 8px', fontSize: 12 }}>
                      🏷️ {tpl.categorie || 'categorie'}
                    </span>
                  )}
                  <span>v{tpl.versie || 1}</span>
                  <span>{new Date(tpl.created_at).toLocaleDateString('nl-NL')}</span>
                </div>
                {/* Actions */}
                <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => duplicateTemplate(tpl.id)} title="Dupliceren"
                    style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📋</button>
                  <button onClick={() => deleteTemplate(tpl.id)} title="Verwijderen"
                    style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 500, margin: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ margin: 0, fontSize: 20 }}>📤 Excel Template Uploaden</h2>
                <button onClick={() => setShowUploadModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af' }}>×</button>
              </div>
              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Excel Bestand *</label>
                  <input type="file" accept=".xlsx,.xls" onChange={e => { setSelectedFile(e.target.files[0]); if (e.target.files[0] && !uploadForm.naam) setUploadForm({ ...uploadForm, naam: e.target.files[0].name.replace(/\.(xlsx|xls)$/i, '') }); }} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Template Naam *</label>
                  <input value={uploadForm.naam} onChange={e => setUploadForm({ ...uploadForm, naam: e.target.value })} placeholder="Bijv. Elektrisch Wissel Onderhoud"
                    style={{ width: '100%', padding: 12, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Beschrijving</label>
                  <textarea value={uploadForm.beschrijving} onChange={e => setUploadForm({ ...uploadForm, beschrijving: e.target.value })} placeholder="Optioneel..." rows={3}
                    style={{ width: '100%', padding: 12, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: '0 0 16px 16px' }}>
                <button onClick={() => setShowUploadModal(false)} style={{ padding: '10px 20px', background: 'white', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' }}>Annuleren</button>
                <button onClick={handleUpload} disabled={uploading || !selectedFile || !uploadForm.naam}
                  style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', opacity: (uploading || !selectedFile || !uploadForm.naam) ? 0.6 : 1 }}>
                  {uploading ? 'Uploaden...' : 'Uploaden'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Template Modal */}
        {showNewModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 450, margin: 20, padding: 24 }}>
              <h2 style={{ margin: '0 0 20px', fontSize: 20 }}>Nieuwe template aanmaken</h2>
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && startNewTemplate()} placeholder="Template naam *"
                style={{ width: '100%', padding: 12, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }} />
              <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Beschrijving (optioneel)"
                style={{ width: '100%', padding: 12, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 20, boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowNewModal(false)} style={{ padding: '10px 20px', background: 'white', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' }}>Annuleren</button>
                <button onClick={startNewTemplate} disabled={!newName.trim()} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: newName.trim() ? 1 : 0.5 }}>Aanmaken & Bewerken</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════
  // ══  RENDER: EDITOR VIEW
  // ══════════════════════════════════════

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Top Bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={goBack} style={{ padding: '7px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#374151' }}>← Terug</button>
        <input value={activeTemplate.naam} onChange={e => updateActive({ naam: e.target.value })}
          style={{ flex: 1, minWidth: 150, background: 'transparent', border: 'none', fontSize: 17, fontWeight: 700, outline: 'none', color: '#1e293b' }} />
        <span style={{ fontSize: 12, color: '#6b7280' }}>{activeTemplate.secties.length} secties · {totalItems} items · {requiredItems} verplicht</span>
        {hasChanges && <span style={{ fontSize: 11, color: '#f97316', fontWeight: 600, background: '#fff7ed', padding: '3px 8px', borderRadius: 4 }}>Niet opgeslagen</span>}
        <button onClick={() => setShowSettings(!showSettings)} style={{ padding: '7px 12px', background: showSettings ? '#e5e7eb' : '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>⚙</button>
        <button onClick={() => setShowPreview(!showPreview)} style={{ padding: '7px 16px', background: showPreview ? '#2563eb' : '#eff6ff', color: showPreview ? '#fff' : '#2563eb', border: showPreview ? 'none' : '1px solid #bfdbfe', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {showPreview ? '✕ Sluit preview' : '📱 Monteur Preview'}
        </button>
        <button onClick={saveTemplate} disabled={saving} style={{ padding: '7px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Opslaan...' : '💾 Opslaan'}
        </button>
      </div>

      {/* Settings */}
      {showSettings && (
        <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Soort</label>
              <select value={activeTemplate.soort || 'checklist'} onChange={e => updateActive({ soort: e.target.value })}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
                <option value="checklist">Checklist (bij werkorder)</option>
                <option value="meet">Meetformulier (monteur kiest zelf)</option>
                <option value="afname">Afnameformulier (monteur kiest zelf)</option>
              </select>
            </div>
            {activeTemplate.soort && activeTemplate.soort !== 'checklist' && (
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Handtekening</label>
                <select value={activeTemplate.handtekening || 'geen'} onChange={e => updateActive({ handtekening: e.target.value })}
                  style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
                  <option value="geen">Geen</option>
                  <option value="optioneel">Optioneel</option>
                  <option value="verplicht">Verplicht</option>
                </select>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Beschrijving</label>
              <input value={activeTemplate.beschrijving} onChange={e => updateActive({ beschrijving: e.target.value })} placeholder="Template beschrijving"
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: 260 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Kleur</label>
              <input type="color" value={activeTemplate.kleur || '#1e40af'} onChange={e => updateActive({ kleur: e.target.value })}
                style={{ width: 40, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: '#6b7280' }}>Twee kolommen (monteur)</label>
              <button onClick={() => updateActive({ twoColumn: !activeTemplate.twoColumn })}
                style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: activeTemplate.twoColumn ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: activeTemplate.twoColumn ? 23 : 3, transition: 'left 0.2s' }} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* LEFT: EDITOR */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 20px 80px' }}>
          {activeTemplate.secties.map((sec, sIdx) => (
            <div key={sec.id}>
              <div onDragOver={e => onSecDropZoneDragOver(e, sIdx)} onDrop={e => onSecDropZoneDrop(e, sIdx)}
                style={{ height: dragSectionIndicator === sIdx ? 4 : 6, background: dragSectionIndicator === sIdx ? '#3b82f6' : 'transparent', borderRadius: 2, transition: 'all 0.15s' }} />

              <div style={{ background: 'white', borderRadius: 12, marginBottom: 4, border: '1px solid #e5e7eb', overflow: 'hidden', opacity: draggingSectionId === sec.id ? 0.4 : 1 }}>
                {/* Section Header */}
                <div draggable onDragStart={e => onSecDragStart(e, sec.id)} onDragEnd={onSecDragEnd}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', background: `linear-gradient(135deg, ${activeTemplate.kleur || '#1e3a5f'}, #3b82f6)`, cursor: 'grab', color: 'white' }}>
                  <span style={{ opacity: 0.5, fontSize: 15 }}>⠿</span>
                  {editingSection === sec.id ? (
                    <input autoFocus value={sec.naam} onChange={e => updateSection(sec.id, { naam: e.target.value })} onBlur={() => setEditingSection(null)} onKeyDown={e => e.key === 'Enter' && setEditingSection(null)}
                      style={{ flex: 1, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, outline: 'none', padding: '4px 8px', borderRadius: 4 }} />
                  ) : (
                    <span onDoubleClick={() => setEditingSection(sec.id)} style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{sec.naam}</span>
                  )}
                  <span style={{ fontSize: 11, opacity: 0.6 }}>{sec.items.length} items</span>
                  <button onClick={() => moveSection(sec.id, -1)} disabled={sIdx === 0} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', opacity: sIdx === 0 ? 0.3 : 1, fontSize: 11 }}>▲</button>
                  <button onClick={() => moveSection(sec.id, 1)} disabled={sIdx === activeTemplate.secties.length - 1} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', opacity: sIdx === activeTemplate.secties.length - 1 ? 0.3 : 1, fontSize: 11 }}>▼</button>
                  <button onClick={() => duplicateSection(sec.id)} title="Dupliceer sectie" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', fontSize: 12 }}>📋</button>
                  <button onClick={() => updateSection(sec.id, { collapsed: !sec.collapsed })} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', fontSize: 11 }}>{sec.collapsed ? '＋' : '−'}</button>
                  {activeTemplate.secties.length > 1 && <button onClick={() => deleteSection(sec.id)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', fontSize: 12 }}>✕</button>}
                </div>

                {/* METINGEN: monteur-instellingen per sectie */}
                {activeTemplate.soort && activeTemplate.soort !== 'checklist' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '8px 16px', background: sec.aanpasbaar ? '#ecfdf5' : '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#374151' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600 }}>
                      <input type="checkbox" checked={!!sec.aanpasbaar}
                        onChange={e => updateSection(sec.id, e.target.checked
                          ? { aanpasbaar: true, min_aantal: sec.min_aantal ?? 1, standaard_aantal: sec.standaard_aantal > 1 ? sec.standaard_aantal : 3, max_aantal: sec.max_aantal > 1 ? sec.max_aantal : 20, naam_invulbaar: sec.naam_invulbaar ?? true }
                          : { aanpasbaar: false })} />
                      Aanpasbaar door monteur
                    </label>
                    {sec.aanpasbaar ? (
                      <>
                        {[['min_aantal', 'min'], ['standaard_aantal', 'standaard'], ['max_aantal', 'max']].map(([k, t]) => (
                          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {t}
                            <input type="number" min={0} max={50} value={sec[k] ?? ''}
                              onChange={e => updateSection(sec.id, { [k]: e.target.value === '' ? '' : Math.max(0, Math.min(50, parseInt(e.target.value, 10) || 0)) })}
                              style={{ width: 52, padding: '3px 6px', borderRadius: 5, border: '1px solid #d1d5db' }} />
                          </label>
                        ))}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!sec.naam_invulbaar} onChange={e => updateSection(sec.id, { naam_invulbaar: e.target.checked })} />
                          Monteur geeft naam (bijv. "Lus wissel 312")
                        </label>
                      </>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Vast — monteur kan niets toevoegen of verwijderen</span>
                    )}
                  </div>
                )}

                {/* Items */}
                {!sec.collapsed && (
                  <div style={{ padding: '8px 12px', minHeight: 36 }}
                    onDragOver={e => onSectionBodyDragOver(e, sec.id)}
                    onDrop={e => onEmptySectionDrop(e, sec.id)}>
                    {sec.items.length === 0 && (
                      <div onDragOver={e => { e.preventDefault(); setDragIndicator({ sectionId: sec.id, index: 0 }); }} onDrop={e => onDropZoneDrop(e, sec.id, 0)}
                        style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12, border: '2px dashed #e5e7eb', borderRadius: 10, background: dragIndicator?.sectionId === sec.id ? '#eff6ff' : 'transparent' }}>
                        Sleep items hierheen of klik '+ Item toevoegen'
                      </div>
                    )}
                    {sec.items.map((item, iIdx) => {
                      const isEdit = editingItem === item.id;
                      const isDragging = draggingItemId === item.id;
                      return (
                        <div key={item.id}>
                          <div onDragOver={e => onDropZoneDragOver(e, sec.id, iIdx)} onDrop={e => onDropZoneDrop(e, sec.id, iIdx)}
                            style={{ height: dragIndicator?.sectionId === sec.id && dragIndicator?.index === iIdx ? 3 : 4, background: dragIndicator?.sectionId === sec.id && dragIndicator?.index === iIdx ? '#3b82f6' : 'transparent', borderRadius: 2, transition: 'all 0.1s', position: 'relative' }}>
                            {dragIndicator?.sectionId === sec.id && dragIndicator?.index === iIdx && (
                              <><div style={{ position: 'absolute', left: -3, top: -3, width: 9, height: 9, borderRadius: '50%', background: '#3b82f6' }} /><div style={{ position: 'absolute', right: -3, top: -3, width: 9, height: 9, borderRadius: '50%', background: '#3b82f6' }} /></>
                            )}
                          </div>
                          <div draggable={!isEdit} onDragStart={e => onItemDragStart(e, sec.id, item.id)} onDragEnd={onItemDragEnd}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: isEdit ? 14 : '9px 12px', background: isEdit ? '#f8fafc' : '#fafafa', borderRadius: 8, border: `1px solid ${isEdit ? '#3b82f6' : '#f3f4f6'}`, cursor: isEdit ? 'default' : 'grab', opacity: isDragging ? 0.3 : 1, transition: 'all 0.12s' }}>
                            {!isEdit && <span style={{ color: '#9ca3af', fontSize: 13, cursor: 'grab', marginTop: 2 }}>⠿</span>}
                            {isEdit ? (
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <input autoFocus value={item.beschrijving} onChange={e => updateItem(sec.id, item.id, { beschrijving: e.target.value })} onKeyDown={e => e.key === 'Enter' && setEditingItem(null)}
                                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #3b82f6', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                <div>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Type:</div>
                                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                    {ITEM_TYPES.map(t => (
                                      <button key={t.value} onClick={() => updateItem(sec.id, item.id, { type: t.value, ...(t.value === 'dropdown' && !item.opties?.length ? { opties: ['Optie 1', 'Optie 2'] } : {}) })}
                                        style={{ padding: '4px 9px', borderRadius: 5, border: item.type === t.value ? `2px solid ${t.color}` : '1px solid #e5e7eb', background: item.type === t.value ? t.color + '15' : 'white', color: item.type === t.value ? t.color : '#6b7280', fontSize: 11, cursor: 'pointer', fontWeight: item.type === t.value ? 700 : 400 }}>
                                        {t.icon} {t.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                {item.type === 'nummer_eenheid' && (
                                  <div><div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Eenheid:</div>
                                    <input value={item.eenheid || ''} onChange={e => updateItem(sec.id, item.id, { eenheid: e.target.value })} placeholder="Hz, V, bar, mm..." style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: 160 }} />
                                  </div>
                                )}
                                {item.type === 'tekst' && (
                                  <div><div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Placeholder:</div>
                                    <input value={item.placeholder || ''} onChange={e => updateItem(sec.id, item.id, { placeholder: e.target.value })} placeholder="Placeholder tekst..." style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: 240 }} />
                                  </div>
                                )}
                                {item.type === 'dropdown' && (
                                  <div><div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Opties:</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                                      {(item.opties || []).map((opt, oi) => (
                                        <span key={oi} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: '#f3f4f6', borderRadius: 5, fontSize: 12, border: '1px solid #e5e7eb' }}>
                                          {opt}<span onClick={() => updateItem(sec.id, item.id, { opties: item.opties.filter((_, x) => x !== oi) })} style={{ cursor: 'pointer', color: '#ef4444', fontWeight: 700, marginLeft: 2 }}>×</span>
                                        </span>
                                      ))}
                                    </div>
                                    <input value={optionInput} onChange={e => setOptionInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && optionInput.trim()) { updateItem(sec.id, item.id, { opties: [...(item.opties || []), optionInput.trim()] }); setOptionInput(''); } }} placeholder="Nieuwe optie + Enter" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, outline: 'none', width: 160 }} />
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <button onClick={() => updateItem(sec.id, item.id, { verplicht: !item.verplicht })} style={{ width: 38, height: 20, borderRadius: 10, border: 'none', background: item.verplicht ? '#f97316' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: item.verplicht ? 21 : 3, transition: 'left 0.15s' }} />
                                  </button>
                                  <span style={{ fontSize: 12, color: item.verplicht ? '#f97316' : '#6b7280' }}>Verplicht</span>
                                </div>
                                <button onClick={() => setEditingItem(null)} style={{ alignSelf: 'flex-start', padding: '7px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Klaar</button>
                              </div>
                            ) : (
                              <>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }} onClick={() => setEditingItem(item.id)}>
                                  <span style={{ fontSize: 13, color: '#1e293b', cursor: 'pointer' }}>{item.beschrijving}</span>
                                  {item.verplicht && <span style={{ fontSize: 10, color: '#f97316', fontWeight: 700 }}>*</span>}
                                </div>
                                {typeTag(item.type)}
                                {item.eenheid && <span style={{ fontSize: 11, color: '#6b7280' }}>{item.eenheid}</span>}
                                <button onClick={() => moveItem(sec.id, item.id, -1)} disabled={iIdx === 0} style={{ background: 'none', border: 'none', color: iIdx === 0 ? '#e5e7eb' : '#9ca3af', cursor: 'pointer', fontSize: 11, padding: 2 }}>▲</button>
                                <button onClick={() => moveItem(sec.id, item.id, 1)} disabled={iIdx === sec.items.length - 1} style={{ background: 'none', border: 'none', color: iIdx === sec.items.length - 1 ? '#e5e7eb' : '#9ca3af', cursor: 'pointer', fontSize: 11, padding: 2 }}>▼</button>
                                <button onClick={() => setEditingItem(item.id)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 13, padding: 2 }}>✏️</button>
                                <button onClick={() => deleteItem(sec.id, item.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 13, padding: 2 }}>✕</button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {sec.items.length > 0 && (
                      <div onDragOver={e => onDropZoneDragOver(e, sec.id, sec.items.length)} onDrop={e => onDropZoneDrop(e, sec.id, sec.items.length)}
                        style={{ height: dragIndicator?.sectionId === sec.id && dragIndicator?.index === sec.items.length ? 3 : 4, background: dragIndicator?.sectionId === sec.id && dragIndicator?.index === sec.items.length ? '#3b82f6' : 'transparent', borderRadius: 2, position: 'relative' }}>
                        {dragIndicator?.sectionId === sec.id && dragIndicator?.index === sec.items.length && (
                          <><div style={{ position: 'absolute', left: -3, top: -3, width: 9, height: 9, borderRadius: '50%', background: '#3b82f6' }} /><div style={{ position: 'absolute', right: -3, top: -3, width: 9, height: 9, borderRadius: '50%', background: '#3b82f6' }} /></>
                        )}
                      </div>
                    )}
                    {showAddItem === sec.id ? (
                      <div style={{ padding: '10px 0' }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Kies item type:</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
                          {ITEM_TYPES.filter(t => t.value !== 'koptekst').map(t => (
                            <button key={t.value} onClick={() => addItem(sec.id, t.value)}
                              style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', color: '#1e293b', fontSize: 12, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.12s' }}
                              onMouseOver={e => { e.currentTarget.style.borderColor = t.color; e.currentTarget.style.background = t.color + '08'; }}
                              onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = 'white'; }}>
                              <span style={{ fontSize: 15 }}>{t.icon}</span>
                              <div><div style={{ fontWeight: 600 }}>{t.label}</div><div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{t.desc}</div></div>
                            </button>
                          ))}
                        </div>
                        <button onClick={() => setShowAddItem(null)} style={{ marginTop: 8, padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Annuleren</button>
                      </div>
                    ) : (
                      <button onClick={() => setShowAddItem(sec.id)}
                        style={{ width: '100%', padding: 9, marginTop: 4, borderRadius: 8, border: '1px dashed #d1d5db', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 12 }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#9ca3af'; }}>
                        ＋ Item toevoegen
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div onDragOver={e => onSecDropZoneDragOver(e, activeTemplate.secties.length)} onDrop={e => onSecDropZoneDrop(e, activeTemplate.secties.length)}
            style={{ height: dragSectionIndicator === activeTemplate.secties.length ? 4 : 6, background: dragSectionIndicator === activeTemplate.secties.length ? '#3b82f6' : 'transparent', borderRadius: 2, marginBottom: 8 }} />

          <button onClick={addSection}
            style={{ width: '100%', padding: 14, borderRadius: 12, border: '2px dashed #d1d5db', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#9ca3af'; }}>
            ＋ Nieuwe sectie toevoegen
          </button>
          <button onClick={openBlokKiezer}
            style={{ width: '100%', padding: 14, marginTop: 10, border: '2px dashed #2563eb', borderRadius: 12, background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            🧱 Uit blokken toevoegen
          </button>
        </div>

        {/* RIGHT: PREVIEW */}
        {showPreview && (
          <div style={{ width: 400, minWidth: 340, borderLeft: '1px solid #e5e7eb', overflow: 'auto', background: '#f3f4f6' }}>
            <div style={{ padding: '14px 18px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>📱 Monteur Preview</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>{activeTemplate.twoColumn ? '2 kolommen' : '1 kolom'}</span>
              </div>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <div style={{ background: `linear-gradient(135deg, ${activeTemplate.kleur || '#1e3a5f'}, #3b82f6)`, padding: '14px 18px', color: '#fff' }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{activeTemplate.naam}</div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>{totalItems} items · {requiredItems} verplicht · 0% ingevuld</div>
                  <div style={{ marginTop: 8, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}><div style={{ width: '0%', height: '100%', background: '#fff', borderRadius: 2 }} /></div>
                </div>
                <div style={{ padding: '10px 12px' }}>
                  {activeTemplate.secties.map(sec => (
                    <div key={sec.id} style={{ marginBottom: 14 }}>
                      <div style={{ background: `linear-gradient(135deg, ${activeTemplate.kleur || '#1e3a5f'}, #3b82f6)`, color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 7 }}>{sec.naam}</div>
                      <div style={activeTemplate.twoColumn ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 } : { display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {sec.items.map(item => (
                          <div key={item.id} style={{ padding: '9px 11px', background: '#f8fafc', borderRadius: 8, border: '1px solid #f0f0f4' }}>
                            <div style={{ fontSize: 12, color: '#374151', marginBottom: 7, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                              {item.beschrijving}{item.verplicht && <span style={{ color: '#f97316', fontSize: 10 }}>*</span>}
                            </div>
                            {renderItemPreview(item)}
                          </div>
                        ))}
                      </div>
                      {sec.items.length === 0 && <div style={{ padding: 10, textAlign: 'center', color: '#d1d5db', fontSize: 11, fontStyle: 'italic' }}>Geen items</div>}
                    </div>
                  ))}
                </div>
                <div style={{ padding: 12, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ background: '#10b981', color: '#fff', padding: 11, borderRadius: 10, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>✓ Checklist Afronden</div>
                </div>
              </div>
            </div>
          </div>
        )}
        {blokKiezer && (
          <div onClick={() => setBlokKiezer(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '88vh', overflow: 'auto', padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <h2 style={{ margin: 0, fontSize: 20 }}>🧱 Blokken kiezen</h2>
                <button onClick={() => setBlokKiezer(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Klik blokken aan; ze worden secties in je template. Zet ze rechts in de juiste volgorde.</p>
              {beschikbareBlokken.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Nog geen blokken. Maak ze eerst via Blokken beheren.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>BESCHIKBAAR</div>
                    {beschikbareBlokken.map(b => {
                      const aantalGekozen = gekozenBlokken.filter(x => x.id === b.id).length;
                      return (
                        <button key={b.id} onClick={() => voegBlokToe(b)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, borderRadius: 8, border: '2px solid', borderColor: aantalGekozen > 0 ? '#2563eb' : '#e2e8f0', background: aantalGekozen > 0 ? '#eff6ff' : 'white', cursor: 'pointer' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: '#111827' }}>{b.naam}{aantalGekozen > 0 ? ' (' + aantalGekozen + '×)' : ''}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{b.aantal_items} punten{b.omschrijving ? ' · ' + b.omschrijving : ''}</div>
                          </div>
                          <span style={{ fontSize: 20, fontWeight: 700, color: '#2563eb', lineHeight: 1 }}>+</span>
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>VOLGORDE IN TEMPLATE</div>
                    {gekozenBlokken.length === 0 ? (
                      <div style={{ padding: 20, textAlign: 'center', color: '#cbd5e1', fontSize: 13, border: '1px dashed #e2e8f0', borderRadius: 8 }}>Nog niets gekozen</div>
                    ) : gekozenBlokken.map((gb, idx) => (
                      <div key={gb.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', marginBottom: 6, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', minWidth: 18 }}>{idx + 1}.</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>{gb.naam}</span>
                        <button onClick={() => verplaatsBlok(idx, -1)} disabled={idx === 0} style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                        <button onClick={() => verplaatsBlok(idx, 1)} disabled={idx === gekozenBlokken.length - 1} style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', opacity: idx === gekozenBlokken.length - 1 ? 0.4 : 1 }}>↓</button>
                        <button onClick={() => verwijderGekozen(gb.key)} style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
                <button onClick={() => setBlokKiezer(false)} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={voegBlokkenIn} disabled={gekozenBlokken.length === 0}
                  style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: gekozenBlokken.length ? '#2563eb' : '#cbd5e1', color: 'white', fontWeight: 700, cursor: gekozenBlokken.length ? 'pointer' : 'default' }}>
                  {gekozenBlokken.length} blok(ken) invoegen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChecklistTemplates;
